<template>
  <el-col :span="24" id="searchresult" v-loading.body="isLoading" element-loading-text="Loading...">
    <ListViewHeader
      title="Search Results"
      ref="listViewHeader"
      :meta="meta"
      :pageSize="50"
      :currentPage="currentPage"
      :changePage="changePage"
      :showFilters="showFilters"
      :toggleFilters="toggleFilters"
      :createRouteName="false"
      :showNewRoute="false"
      >
      <!-- Start of Export Button  -->
      <el-button v-if="meta && meta.shown && !changingPage" :disabled="isExporting" type="primary" @click="exportReport">Export Results <b-spinner class="mr-2" small v-if="isExporting"/></el-button>
      <!-- End of Export Button -->
    </ListViewHeader>
    <el-row type="flex" justify="center" align="middle" style="padding: 0 12px;">
      <Filters
        ref="filters"
        :filters="filters"
        :filtersVisible="showFilters"
        :filterSize="4"
        :autoApply="false"
        clearAction="clearSearchResultsState"
        clearMetadataAction="resetSearchResultsMeta"
        searchAction="fetchSearchData"
        @filterChanged="handleFilterChange"
        @filterCleared="() => $refs.filters.applyFilters()"
        @applied="handleAfterApply"
        :key="renderFilter"
        >
        <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="slotProps">
          <component :key="i" :is="f.type" :ref="`${f.type+'_ref'}`" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col>
          <search-results-ag-grid  v-if="meta && meta.shown && !changingPage" 
          name="searchResults" :iteration="renderFilter" :rowData="searchresult" 
          :meta="meta" :isLoading="isLoading" width="100%" height="600px" />
         <el-row v-if="meta.count && !searchresult.length" align="middle" justify="center" class="nodata-heading">
          No data available for applied filters
        </el-row>
        <el-row v-if="!meta.count && !searchresult.length" align="middle" justify="center" class="nodata-heading">
          No data available
        </el-row>
      </el-col>
    </el-row>
  </el-col>
</template>

<script>
  import store from '@/store/store'
  import { mapGetters } from 'vuex'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import ListViewHeader from '@/components/DataEntryListViewHeader'
  import { listViewMixins } from '@/Mixins/TransactionListViews'
  import SearchResultsAgGrid from './SearchResultsAgGrid.vue'
  import { formatAmountForCSV } from '@/utils/helpers/AmountFormatter'

  export default {
    components: {Filters, KeywordFilter, ListViewHeader, SearchResultsAgGrid},
    mixins: [ listViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        statusValues: [],
        watchRoute: true,
        renderFilter: 0,
        keywordSearch: '',
        uniqueAccounts: [],
        loading: false,
        isApiLoading: false,
        isExporting: false
      }
    },
    methods: {
      // Prepare Search Params for Export
      // EXample: Search: 12 | Account: 1 | 2 | Vendor: 1 - Vendor1, 2 - Vendor2 | Source: AP, AR | Document #: 123 | Payment #: 456
      prepSearchParams () {
        let filters = this.$store.getters.getFilters || {}
        // let searchParams = filters.search ? `Keyword: ${filters.search}` : ''
        return filters.search
      },

      // Export Results to Excel
      async exportReport () {
        try {
          this.isExporting = true

          const payload = {
            search: this.keywordSearch || this.searchKey || this.$store.getters.getFilters.search,
            pagination: false
          }

          const entities = await this.$store.dispatch('fetchSearchDataToExport', payload)
          const fullData = Object.values(entities || {})
          if (fullData.length === 0) {
            this.$message({
              message: 'Unable to export. No data available for applied filters.',
              type: 'warning'
            })
            return
          }
          const csv = this.buildCSV(fullData)
          this.downloadCSV(csv)
        } catch (e) {
          console.log(e)
        } finally {
          this.isExporting = false
        }
      },
      buildCSV (rows) {
        const params = this.prepSearchParams()
        const reportDate = new Date().toLocaleDateString()

        const headers = [
          'Trans #',
          'Type',
          'Account',
          'Description',
          'Vendor',
          'Doc #',
          'Date',
          'Status',
          'PO #',
          'Payment #',
          'Amount'
        ]

        const escape = (val, field = '') => {
          const amountFields = ['amount', 'debits', 'credits']
          if (field && amountFields.includes(field)) {
            // CSV amounts: US standard, decimal point only — no thousands separator (10000 → 10000.00)
            const formatted = formatAmountForCSV(val)
            val = formatted === '' ? '0.00' : formatted
          } else if (!val) {
            val = ''
          } else if (typeof val === 'string') {
            val = '"' + val.replace(/"/g, '""').replace(/^0(.*$)/, '0$1\t') + '"'
          }

          return val
        }

        // ---- METADATA ROWS ----
        const metaRows = [
          `,Keyword: ${escape(params)},,,,,,,,Report Run Date: ${escape(reportDate)}`,
          '' // empty spacer row
        ]

        // Map a single transaction (or DC line) to CSV cells, matching the
        // list view column order: Trans #, Type, Account, Description, Vendor,
        // Doc #, Date, Status, PO #, Payment #, Amount.
        const rowToLine = r => [
          escape(r.transaction_ledger ? r.transaction_ledger.split(' ')[0] : '--'),
          escape(r.transaction_src || '--'),
          escape(r.account || '--'),
          escape(r.description || '--'),
          escape(r.transaction_vendor || r.transaction_third_party_vendor || '--'),
          escape(r.transaction_number || '--'),
          escape(r.transaction_date || '--'),
          escape(r.transaction_status || '--'),
          escape(r.PO_number || r.PO_reference || '--'),
          escape(r.transaction_payment_number || '--'),
          escape(r.amount || 0, 'amount')
        ].join(',')

        // ---- DATA ROWS ----
        // Rows carrying dc_lines (the expand option) emit their detail lines
        // right after the parent row so post-DC changes are reflected in export.
        const lines = rows.reduce((acc, r) => {
          acc.push(rowToLine(r))
          if (Array.isArray(r.dc_lines)) {
            r.dc_lines.forEach(line => acc.push(rowToLine(line)))
          }
          return acc
        }, [])

        return [
          ...metaRows,
          headers.join(','),
          ...lines
        ].join('\n')
      },
      downloadCSV (csv) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = 'Transactions.csv'
        document.body.appendChild(link)
        link.click()

        this.$message({
          message: 'Export successful!',
          type: 'success'
        })
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      },
      async handleFromRouteChange (key) {
        if (key && key !== this.searchKey && !this.isApiLoading) {
          this.isApiLoading = true
          await this.$store.dispatch('clearSearchResultsState', [])
          await this.$store.dispatch('resetSearchResultsMeta')
          this.$store.dispatch('setFilter', { search: key })
          if (this.$refs['filters'] && this.$refs['filters'].syncKeyword) this.$refs['filters'].syncKeyword(key)
          await this.$store.dispatch('updateSearchkey', key)
          this.isApiLoading = false
        }
      },
      handleTableLoad () {
        const parentHeight = document.getElementById('searchresult-table').offsetHeight
        const headerHeight = document.getElementById('list-view-header').offsetHeight
        this.tableHeight = parentHeight - headerHeight
      },
      handleAfterApply () {
        if (this.keywordSearch === this.$store.getters.getFilters.search) return
        this.keywordSearch = this.$store.getters.getFilters.search
      },
      handleFilterChange (filter) {
        this.$store.dispatch('setFilter', { [`search`]: filter.selectedVal })
      },
      getTransactionRouteName (src) {
        if (!src) return
        let routeName = {
          'AP': 'EditAp',
          'AR': 'EditCpaar',
          'RT': 'EditRt',
          'PC': 'EditPc',
          'CC': 'EditCc',
          'JE': 'EditJe',
          'PR': 'EditCpr',
          'PO': 'EditPo'
        }
        return routeName[src]
      },
      /**
       * Handles paginating between AP pages
       * @param page  Target page
       */
      async changePage (page) {
        try {
          this.changingPage = true
          const { currentFilters } = this
          let payload = currentFilters && Object.keys(currentFilters).length
            ? Object.assign({}, { ...currentFilters, page })
            : Object.assign({}, { page })
          await this.$store.dispatch('clearSearchResultsState', [])
          const entities = await this.$store.dispatch('fetchSearchData', payload)
          if (entities) {
            const promises = await this.$store.dispatch('findDependencies', entities)
            await Promise.all(promises)
          }
          await new Promise((resolve, reject) => {
            setTimeout(() => {
              this.changingPage = false
              resolve()
            })
          })
        } catch (e) {
          console.log(e) // TODO: use a logger
          this.changingPage = false
        }
      },
      formatDateForExport (date) {
        date = date.replace(/-/g, '/')
        return new Date(date).toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' })
      },
      intlDateFormatter (date) {
        if (date) {
          const dt = date.split('-')
          console.log('dt: ', dt) // TODO: remove
          return dt[1] + '-' + dt[2] + '-' + dt[0]
        } else {
          return ''
        }
      }
    },
    computed: {
      ...mapGetters({
        getVendorById: 'getVendorById',
        searchresultRaw: 'getSearchResults',
        meta: 'getSearchResultsMetadata',
        currentPage: 'getCurrentSearchResultsPage',
        currentFilters: 'getFilters',
        isLoading: 'getReceivingDataStatus'
      }),
      searchKey () {
        let search = this.$store.getters.getSearchKey || ''
        if (Object.keys(this.$refs).length) {
          setTimeout(() => {
            this.$refs['keywordFilter_ref'][0].keyword = search
            this.$store.dispatch('setFilter', { search })
          }, 10)
        }
        return search
      },
      searchresult () {
        return Array.isArray(this.searchresultRaw) ? this.searchresultRaw : Object.values(this.searchresultRaw || {})
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {
              keyword: this.searchKey
            }
          }
        ]
      }
    },
    watch: {
      '$route.query': {
        async handler (newQuery, oldQuery) {
          let key = newQuery.searchKey
          if ((!oldQuery || !oldQuery.searchKey || oldQuery.searchKey !== key) && key) {
            await this.handleFromRouteChange(key)
          }
        },
        deep: true,
        immediate: true
      }
    },
    async mounted () {
      if (Object.keys(this.$refs).length) {
        this.$refs['keywordFilter_ref'][0].keyword = this.searchKey
        this.$store.dispatch('setFilter', { search: this.searchKey })
      }
      let key = this.$route.query.searchKey
      await this.handleFromRouteChange(key)
    },
    async beforeDestroy () {
      await Promise.all([
        this.$store.dispatch('clearFilters')
        // this.$store.dispatch('updateTransactionSelection', []),
        // this.$store.dispatch('updateFilteredTransactionSelection', [])
      ])
    },
    async beforeRouteLeave (to, from, next) {
      try {
        await store.dispatch('clearPoState', true)
        // Clear the watcher before leaving the route
        this.watchRoute = false
        this.renderFilter = 0
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  a, a:visited {
    text-decoration: none;
    color: rgb(0, 0, 238) !important;
  }

  .wrap-text {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
  }
</style>


// WEBPACK FOOTER //
// src/components/RouteComponents/SearchResults.vue